import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import { notificationService } from "../../services/api";
import styles from "./NotificationBell.module.css";

const POLL_MS = 60000;

/** Sección del perfil (o ruta) a la que lleva cada notificación al hacer clic. */
export function notificationTarget(n) {
  const type = n?.data?.type || "";
  const event = n?.event_type || "";
  if (type === "bonus_granted" || event === "BONUS_GRANTED") return { path: "/perfil", section: "bonos" };
  if (type.startsWith("lottery_") || event === "LOTTERY_RESULT") return { path: "/apuestas" };
  if (event === "BET_WON") return { path: "/perfil", section: "ganancias" };
  if (n?.data?.withdrawal_id || event.startsWith("WITHDRAWAL")) return { path: "/perfil", section: "retiros" };
  if (event.startsWith("IDENTITY")) return { path: "/perfil", section: "datos" };
  if (event.startsWith("RAFFLE") || type.startsWith("raffle")) return { path: "/perfil", section: "rifas" };
  if (/recarga/i.test(n?.title || "")) return { path: "/perfil", section: "recargas" };
  return null;
}

export function timeAgo(iso, now = Date.now()) {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const min = Math.max(0, Math.round((now - t) / 60000));
  if (min < 1) return "Ahora";
  if (min < 60) return `Hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `Hace ${h} h`;
  const d = Math.round(h / 24);
  if (d < 7) return d === 1 ? "Ayer" : `Hace ${d} días`;
  return new Date(t).toLocaleDateString("es-CO", { day: "numeric", month: "short" });
}

/** Campanita con el buzón de notificaciones (las mismas que recibe la app por push). */
export default function NotificationBell() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const refreshCount = useCallback(async () => {
    const res = await notificationService.unreadCount();
    if (res.ok) setUnread(res.count);
  }, []);

  useEffect(() => {
    refreshCount();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") refreshCount();
    }, POLL_MS);
    const onFocus = () => refreshCount();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next) return;
    setLoading(true);
    setError("");
    const res = await notificationService.list({ pageSize: 20 });
    setLoading(false);
    if (res.ok) setItems(res.data);
    else setError(res.message);
  };

  const markAll = async () => {
    const res = await notificationService.markAllRead();
    if (res.ok) {
      setItems((list) => list.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
    }
  };

  const openItem = (n) => {
    if (!n.is_read) {
      notificationService.markRead(n.id);
      setItems((list) => list.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      setUnread((c) => Math.max(0, c - 1));
    }
    const target = notificationTarget(n);
    if (target) {
      setOpen(false);
      navigate(target.path, target.section ? { state: { section: target.section } } : undefined);
    }
  };

  const label = unread > 0 ? `Notificaciones, ${unread} sin leer` : "Notificaciones";

  return (
    <div className={styles.wrap} ref={ref}>
      <button
        type="button"
        className={`${styles.bell} ${open ? styles.bellActive : ""}`}
        onClick={toggle}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Notificaciones"
      >
        <FontAwesomeIcon icon={faBell} />
        {unread > 0 && <span className={styles.count}>{unread > 99 ? "99+" : unread}</span>}
      </button>

      {open && (
        <div className={styles.panel} role="dialog" aria-label="Notificaciones">
          <div className={styles.head}>
            <span className={styles.title}>Notificaciones</span>
            {items.some((n) => !n.is_read) && (
              <button type="button" className={styles.markAll} onClick={markAll}>
                Marcar todas como leídas
              </button>
            )}
          </div>
          <div className={styles.list}>
            {loading && <p className={styles.empty}>Cargando…</p>}
            {!loading && error && <p className={styles.empty}>{error}</p>}
            {!loading && !error && items.length === 0 && (
              <p className={styles.empty}>No tienes notificaciones por ahora.</p>
            )}
            {!loading &&
              !error &&
              items.map((n) => (
                <button
                  type="button"
                  key={n.id}
                  className={`${styles.item} ${n.is_read ? "" : styles.unread}`}
                  onClick={() => openItem(n)}
                >
                  {!n.is_read && <span className={styles.dot} aria-label="Sin leer" />}
                  <span className={styles.itemTitle}>{n.title}</span>
                  {n.body && <span className={styles.itemBody}>{n.body}</span>}
                  <span className={styles.itemTime}>{timeAgo(n.sent_at || n.created_at)}</span>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
